import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addNotification } from '../../../store/slices/uiSlice';
import { fetchUsers } from '../../../store/slices/userSlice';
import { WIZARD_STEPS, visibleWizardSteps } from '../constants/wizardSteps';
import { createDefaultEventForm, eventDtoToForm, formToApiPayload, formToUpdatePayload, applyTemplateToForm } from '../utils/eventDefaults';
import { followUpEventName, pollDisplayName } from '../utils/pollUtils';
import { validateStep, validateAllSteps } from '../utils/eventValidation';
import { createEvent, updateEvent } from '../services/eventService';
import { getEventTemplate } from '../constants/eventTemplates';
import { DEFAULT_EXPENSE_CONFIGURATION } from '../constants/expenseFields';
import EventWizardStepper from './EventWizardStepper';
import FollowUpEventPrompt from './FollowUpEventPrompt';
import {
  BasicInformationStep,
  ParticipantsStep,
  RulesResponsesStep,
  RemindersNotificationsStep,
  CompletionActionsStep,
  AttachmentsStep,
  ReviewStep,
} from './WizardSteps';

const CreateEventWizard = ({
  isOpen,
  onClose,
  onCreated,
  channel,
  otherUser,
  personalMode = false,
  schedulePreset = null,
  editingEvent = null,
  initialEventType = null,
  initialParticipants = null,
  skipPostedFollowUp = false,
  sourceEventName = '',
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const allUsers = useSelector((state) => state.users?.users || []);
  const [step, setStep] = useState('basic');
  const [form, setForm] = useState(() => createDefaultEventForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [pendingCreated, setPendingCreated] = useState(null);
  const idempotencyRef = useRef(`event-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);

  const isDm = channel?.type === 'direct' || channel?.type === 'DIRECT_MESSAGE';
  const contextType = personalMode ? 'PERSONAL' : isDm ? 'DM' : 'GROUP';
  const channelId = personalMode ? '' : channel?.id;

  const directoryUsers = useMemo(
    () => (allUsers || []).filter((u) => u?.id && u.isActive !== false),
    [allUsers],
  );

  const channelMembers = useMemo(() => {
    if (personalMode || !channel?.memberIds?.length) return directoryUsers;
    return directoryUsers.filter((u) => channel.memberIds.includes(u.id));
  }, [personalMode, channel, directoryUsers]);

  const isEditing = Boolean(editingEvent?.id);
  const isDraftEdit = isEditing && editingEvent?.status === 'DRAFT';
  const visibleSteps = useMemo(
    () => visibleWizardSteps(form),
    [form.eventType, form.schedule?.startDate],
  );

  useEffect(() => {
    if (!visibleSteps.some((item) => item.id === step)) {
      setStep(visibleSteps[0]?.id || 'basic');
    }
  }, [visibleSteps, step]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchUsers({ force: true, fromNetwork: true }));
      const defaults = {
        channelId,
        contextType,
        publishMode: personalMode ? 'ANNOUNCEMENT' : 'CHANNEL',
        schedulePreset,
        userId: user?.id,
        otherUserId: otherUser?.id,
      };
      if (editingEvent?.id) {
        setForm(eventDtoToForm(editingEvent, defaults));
        setCompletedSteps(WIZARD_STEPS.map((s) => s.id));
      } else {
        const next = createDefaultEventForm(defaults);
        if (initialEventType === 'POLL') {
          next.eventType = 'POLL';
          next.schedule = {
            ...next.schedule,
            startDate: schedulePreset?.startDate || '',
            startTime: schedulePreset?.startTime || '',
            endDate: schedulePreset?.endDate || '',
            endTime: schedulePreset?.endTime || '',
            expiresAt: schedulePreset?.expiresAt || '',
            expiresAtTime: schedulePreset?.expiresAtTime || '',
          };
          next.responsePolicy = {
            ...next.responsePolicy,
            type: 'POLL',
            pollQuestion: '',
            pollOptions: ['', ''],
            allowMultiple: false,
          };
          if (contextType !== 'DM') {
            next.participants = {
              ...next.participants,
              mode: 'SELECT',
              optional: [],
              required: (next.participants?.required || []).filter((p) => String(p.userId) === String(user?.id)),
            };
          }
        } else if (initialEventType === 'ACTION_TRACKING') {
          next.eventType = 'ACTION_TRACKING';
          next.eventTypeChosen = true;
          next.category = 'PERSONAL';
          next.responsePolicy = { ...next.responsePolicy, type: 'NONE' };
          next.configuration = {
            trackKind: '',
            entities: [],
            entries: [],
            actionShare: false,
          };
          next.participants = {
            ...next.participants,
            mode: 'SELF',
            allowInviteOthers: true,
            optional: [],
            required: (next.participants?.required || []).filter((p) => String(p.userId) === String(user?.id)),
          };
        } else if (initialEventType) {
          next.eventType = initialEventType;
          next.eventTypeChosen = true;
          if (sourceEventName) {
            next.name = followUpEventName(sourceEventName, initialEventType);
          }
          if (initialEventType === 'SPLITWISE') {
            next.configuration = {
              currency: 'INR',
              splitMethod: 'PERCENTAGE',
              whoCanAddExpenses: 'ALL',
              fields: { image: { enabled: true, required: false } },
            };
            next.category = 'FINANCE';
          }
          if (initialEventType === 'EXPENSE_TRACKING') {
            next.responsePolicy = { ...next.responsePolicy, type: 'NONE' };
            next.configuration = { ...DEFAULT_EXPENSE_CONFIGURATION };
            next.category = 'FINANCE';
          }
        }
        if (Array.isArray(initialParticipants) && initialParticipants.length) {
          next.participants = {
            ...next.participants,
            mode: 'SELECT',
            required: initialParticipants,
            optional: [],
            guests: [],
          };
        }
        setForm(next);
        idempotencyRef.current = `event-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        setCompletedSteps([]);
      }
      setStep('basic');
      setErrors({});
      setFollowUpOpen(false);
      setPendingCreated(null);
    }
  }, [isOpen, channelId, contextType, personalMode, schedulePreset, user?.id, otherUser?.id, editingEvent, initialEventType, initialParticipants, sourceEventName, dispatch]);

  useEffect(() => {
    if (!isOpen || isEditing) return;
    const mode = form.participants?.mode;
    if (form.eventType === 'POLL' && mode === 'SELECT') return;
    if (mode !== 'EVERYONE' && mode !== 'ADMINS_ONLY' && mode !== 'DM_AUTO') return;
    const pool = personalMode ? directoryUsers : (channelMembers.length ? channelMembers : directoryUsers);
    if (!pool.length) return;
    setForm((prev) => {
      const prevMode = prev.participants?.mode;
      if (prevMode !== 'EVERYONE' && prevMode !== 'ADMINS_ONLY' && prevMode !== 'DM_AUTO') return prev;
      const existingIds = new Set((prev.participants?.required || []).map((p) => String(p.userId)));
      const extra = pool
        .filter((u) => !existingIds.has(String(u.id)))
        .map((u) => ({
          userId: u.id,
          role: 'REQUIRED',
          status: String(u.id) === String(user?.id) ? 'ACCEPTED' : 'INVITED',
        }));
      if (!extra.length) return prev;
      return {
        ...prev,
        participants: {
          ...prev.participants,
          required: [...(prev.participants.required || []), ...extra],
        },
      };
    });
  }, [isOpen, personalMode, isEditing, directoryUsers, channelMembers, user?.id, form.participants?.mode]);

  const patchForm = useCallback((patch) => {
    setForm((prev) => {
      let next = { ...prev, ...patch };
      if (patch.templateId) {
        const tpl = getEventTemplate(patch.templateId);
        if (tpl) {
          next = applyTemplateToForm(next, tpl);
          next.eventTypeChosen = true;
        }
      }
      if (patch.eventType) next.eventTypeChosen = true;
      if (patch.schedule) next.schedule = { ...prev.schedule, ...patch.schedule };
      if (patch.participants) next.participants = { ...prev.participants, ...patch.participants };
      if (patch.responsePolicy) next.responsePolicy = { ...prev.responsePolicy, ...patch.responsePolicy };
      if (patch.reminderPolicy) next.reminderPolicy = { ...prev.reminderPolicy, ...patch.reminderPolicy };
      if (patch.notificationPolicy) next.notificationPolicy = { ...prev.notificationPolicy, ...patch.notificationPolicy };
      if (patch.permissions) next.permissions = { ...prev.permissions, ...patch.permissions };
      if (patch.visibility) next.visibility = { ...prev.visibility, ...patch.visibility };
      if (patch.location) next.location = { ...prev.location, ...patch.location };
      if (patch.eventType === 'POLL' && !isEditing) {
        next.schedule = {
          ...next.schedule,
          startDate: '',
          startTime: '',
          endDate: '',
          endTime: '',
          expiresAt: '',
          expiresAtTime: '',
        };
      }
      if (patch.configuration) {
        next.configuration = patch.eventType
          ? patch.configuration
          : { ...prev.configuration, ...patch.configuration };
      }
      return next;
    });
  }, [isEditing]);

  const goNext = () => {
    const result = validateStep(step, form);
    if (!result.valid) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    setCompletedSteps((prev) => (prev.includes(step) ? prev : [...prev, step]));
    const idx = visibleSteps.findIndex((s) => s.id === step);
    if (idx < visibleSteps.length - 1) setStep(visibleSteps[idx + 1].id);
  };

  const goBack = () => {
    const idx = visibleSteps.findIndex((s) => s.id === step);
    if (idx > 0) setStep(visibleSteps[idx - 1].id);
  };

  const submit = async (draft = false) => {
    if (!draft) {
      const all = validateAllSteps(form);
      if (!all.valid) {
        setStep(all.stepId);
        setErrors(all.errors);
        return;
      }
    } else if (!form.name?.trim()) {
      setErrors({ name: 'Event name is required to save draft' });
      setStep('basic');
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (isEditing) {
        const payload = formToUpdatePayload({
          ...form,
          channelId: form.channelId || channelId,
        }, { draft, editingStatus: editingEvent?.status });
        saved = await updateEvent(editingEvent.id, payload);
      } else {
        const payload = formToApiPayload({
          ...form,
          channelId: form.channelId || channelId,
        }, {
          draft,
          idempotencyKey: idempotencyRef.current,
        });
        saved = await createEvent(payload);
      }
      dispatch(addNotification({
        type: 'success',
        message: draft
          ? 'Draft saved'
          : isEditing && !isDraftEdit
            ? `"${saved.name}" updated`
            : `"${saved.name}" published`,
      }));
      if (!draft && saved?.eventType === 'POLL' && !isEditing && !skipPostedFollowUp) {
        onCreated?.(saved);
        setPendingCreated(saved);
        setFollowUpOpen(true);
        return;
      }
      onCreated?.(saved);
      onClose?.();
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.response?.data?.error || (isEditing ? 'Failed to update event' : 'Failed to create event'),
      }));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const stepProps = { form, onChange: patchForm, errors };

  const renderStep = () => {
    switch (step) {
      case 'basic':
      case 'schedule': // schedule lives inside Basic now
        return (
          <BasicInformationStep
            {...stepProps}
            users={directoryUsers}
            currentUser={user}
            isDm={isDm}
            personalMode={personalMode}
            channelMembers={channelMembers}
            lockEventType={Boolean(
              isEditing
              || sourceEventName
              || skipPostedFollowUp
              || (initialEventType && initialEventType !== 'POLL')
            )}
          />
        );
      case 'participants': return (
        <ParticipantsStep
          {...stepProps}
          users={directoryUsers}
          currentUser={user}
          isDm={isDm}
          personalMode={personalMode}
          channelMembers={channelMembers}
        />
      );
      case 'rules': return <RulesResponsesStep {...stepProps} />;
      case 'notifications': return <RemindersNotificationsStep {...stepProps} />;
      case 'completion': return <CompletionActionsStep {...stepProps} />;
      case 'attachments': return <AttachmentsStep {...stepProps} />;
      case 'review': return <ReviewStep form={form} onEditStep={(id) => setStep(id === 'schedule' ? 'basic' : id)} />;
      default: return null;
    }
  };

  const isPoll = form.eventType === 'POLL';
  const isAction = form.eventType === 'ACTION_TRACKING';
  const isCompact = isPoll || isAction;
  const isLast = isCompact || step === 'review';
  const isPublishedEdit = isEditing && !isDraftEdit;
  const primaryLabel = saving
    ? (isPublishedEdit ? 'Saving…' : 'Publishing…')
    : (isPublishedEdit ? 'Save event' : isPoll ? 'Post poll' : isAction ? 'Create tracker' : 'Publish');

  const startFollowUpType = (typeId) => {
    const defaults = {
      channelId,
      contextType,
      schedulePreset,
      userId: user?.id,
      otherUserId: otherUser?.id,
    };
    const next = createDefaultEventForm(defaults);
    next.eventType = typeId;
    next.eventTypeChosen = true;
    next.name = followUpEventName(pollDisplayName(pendingCreated), typeId);
    if (typeId === 'SPLITWISE') {
      next.configuration = {
        currency: 'INR',
        splitMethod: 'PERCENTAGE',
        whoCanAddExpenses: 'ALL',
        fields: { image: { enabled: true, required: false } },
      };
      next.category = 'FINANCE';
    }
    if (typeId === 'EXPENSE_TRACKING') {
      next.responsePolicy = { ...next.responsePolicy, type: 'NONE' };
      next.configuration = { ...DEFAULT_EXPENSE_CONFIGURATION };
      next.category = 'FINANCE';
    }
    setForm(next);
    setStep('basic');
    setErrors({});
    setCompletedSteps([]);
    setFollowUpOpen(false);
    setPendingCreated(null);
    idempotencyRef.current = `event-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  };

  return ReactDOM.createPortal(
    <>
    <div className={`fixed inset-0 z-[1200] flex flex-col bg-black/50 ${followUpOpen ? 'hidden' : ''}`}>
      <div className="flex flex-col w-full h-full sm:h-auto sm:max-h-[92vh] sm:my-auto sm:mx-auto sm:max-w-3xl sm:rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {isEditing ? (isDraftEdit ? 'Edit draft' : 'Edit event') : isPoll ? 'Create poll' : isAction ? 'Action tracking' : 'Create New Event'}
            </h2>
            {!isCompact && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Step {Math.max(visibleSteps.findIndex((s) => s.id === step) + 1, 1)} of {visibleSteps.length}</p>
            )}
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="text-gray-400 hover:text-gray-600 p-2" aria-label="Close">✕</button>
        </div>

        {!isCompact && (
        <EventWizardStepper
          currentStep={step}
          steps={visibleSteps}
          onStepClick={(id) => {
            setErrors({});
            setStep(id);
          }}
          completedSteps={completedSteps}
          allowAllSteps={isEditing}
        />
        )}

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 min-h-0">
          {renderStep()}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/80">
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">Cancel</button>
            {(!isEditing || isDraftEdit) && !isCompact && (
            <button type="button" onClick={() => submit(true)} disabled={saving} className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
              Save draft
            </button>
            )}
          </div>
          <div className="flex gap-2">
            {!isCompact && step !== 'basic' && (
              <button type="button" onClick={goBack} disabled={saving} className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">Back</button>
            )}
            {!isCompact && !isLast ? (
              <button type="button" onClick={goNext} disabled={saving} className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700">Next</button>
            ) : (
              <button type="button" onClick={() => submit(false)} disabled={saving} className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                {primaryLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    <FollowUpEventPrompt
      isOpen={followUpOpen}
      title="Poll posted"
      description="Do you want to create another event, like expense sharing?"
      includePoll={false}
      onClose={() => {
        setFollowUpOpen(false);
        setPendingCreated(null);
        onClose?.();
      }}
      onSelectType={startFollowUpType}
    />
    </>,
    document.body,
  );
};

export default CreateEventWizard;
